/**
 * VPV Buy Bar
 * Sticky add-to-cart bar that syncs with buy box card selection
 * 
 * Listens for:
 * - vpv:card:selected - When a buy box card is selected
 * - vpv:variant:changed - When color/variant changes
 * - variant:changed - Legacy variant change event
 */

class VpvBuyBar extends HTMLElement {
  constructor() {
    super();
    
    // Elements
    this.priceEl = this.querySelector('[data-vpv-bar-price]');
    this.optionEl = this.querySelector('[data-vpv-bar-option]');
    this.addButton = this.querySelector('[data-vpv-bar-add-to-cart]');
    
    // State - initialized from data attributes
    this.variantId = this.dataset.variantId;
    this.sellingPlanId = this.dataset.sellingPlanId || null;
    this.isSubscription = this.dataset.isSubscription === 'true';
    this.isLinkedProduct = false;
    this.linkedVariantsMap = null;
    
    this.init();
  }

  init() {
    // Scroll visibility
    window.addEventListener('scroll', this.handleScroll.bind(this));
    
    // Listen for buy box card selection
    document.addEventListener('vpv:card:selected', (e) => this.handleCardSelected(e.detail));
    
    // Listen for variant changes (color swatches)
    window.addEventListener('vpv:variant:changed', (e) => this.handleVariantChange(e.detail));
    window.addEventListener('variant:changed', (e) => this.handleVariantChange(e.detail));
    
    // Add to cart button
    if (this.addButton) {
      this.addButton.addEventListener('click', (e) => this.handleAddToCart(e));
    }

    // Track current option title and color
    this.currentOptionTitle = this.optionEl?.textContent?.trim()?.split('·')[0]?.trim() || '';
    this.currentColor = this.dataset.initialColor || null;

    // Sync with any already-selected card on page load
    this.syncWithActiveCard();
    
    // If no initial color from data attribute, try to get from swatch
    if (!this.currentColor) {
      this.syncColorFromSwatch();
    }
    
    console.log('[VPV Buy Bar] Initialized');
  }

  /**
   * Get the currently selected color from swatch
   */
  syncColorFromSwatch() {
    // Look specifically for color swatch elements, not generic selected elements
    const selectedSwatch = document.querySelector(
      '.vpv-product__variant-picker .swatch-input:checked, ' +
      '.product__variant-picker .swatch-input:checked, ' +
      'variant-radios .swatch-input:checked, ' +
      '[data-option-index="0"] .swatch-input:checked, ' +
      '.color-swatch.is-active, ' +
      '.color-swatch--selected'
    );
    
    if (selectedSwatch) {
      // Get color from data attributes first (most reliable)
      let color = selectedSwatch.dataset?.limited || 
                  selectedSwatch.dataset?.color ||
                  selectedSwatch.dataset?.value ||
                  selectedSwatch.value;
      
      // Only use textContent as fallback, and make sure it's not HTML
      if (!color && selectedSwatch.textContent) {
        const text = selectedSwatch.textContent.trim();
        // Skip if it looks like HTML or is too long (probably grabbed wrong element)
        if (!text.includes('<') && !text.includes('>') && text.length < 50) {
          color = text;
        }
      }
      
      if (color) {
        this.currentColor = color;
        this.updateOptionDisplay();
      }
    }
  }

  /**
   * Update the option display with title and color
   */
  updateOptionDisplay() {
    if (!this.optionEl) return;
    
    let displayText = this.currentOptionTitle || 'One-time purchase';
    
    if (this.currentColor) {
      displayText += ' · ' + this.currentColor;
    }
    
    this.optionEl.textContent = displayText;
  }

  /**
   * Show/hide bar based on scroll position past main product section
   */
  handleScroll() {
    const mainProduct = document.querySelector('.main-product, .product-wrapper, [data-product-section]');
    if (!mainProduct) return;

    const threshold = mainProduct.offsetTop + mainProduct.offsetHeight + 100;
    
    if (window.scrollY >= threshold) {
      this.classList.remove('hide');
    } else {
      this.classList.add('hide');
    }
  }

  /**
   * Sync state with the currently active buy box card
   */
  syncWithActiveCard() {
    const activeCard = document.querySelector('.vpv-buy-box-card--active');
    if (!activeCard) return;

    const variantData = activeCard.querySelector('.vpv-buy-box-card__variant-data');
    if (!variantData) return;

    // Get card data
    const cardTitle = activeCard.querySelector('.vpv-buy-box-card__title');
    const priceEl = activeCard.querySelector('[data-vpv-current-price]');

    // Update state
    this.variantId = variantData.dataset.variantId;
    this.sellingPlanId = variantData.dataset.sellingPlanId || null;
    this.isSubscription = variantData.dataset.isSubscription === 'true';
    this.isLinkedProduct = activeCard.dataset.isLinkedProduct === 'true';
    
    // Store linked variants map if available
    if (this.isLinkedProduct && activeCard.dataset.linkedVariants) {
      try {
        this.linkedVariantsMap = JSON.parse(activeCard.dataset.linkedVariants);
      } catch (e) {
        this.linkedVariantsMap = null;
      }
    } else {
      this.linkedVariantsMap = null;
    }

    // Update option title (color will be added by syncColorFromSwatch)
    if (cardTitle) {
      this.currentOptionTitle = cardTitle.textContent.trim();
    }
    
    // Update price
    if (this.priceEl && priceEl) {
      this.priceEl.textContent = priceEl.textContent.trim();
    }
  }

  /**
   * Handle buy box card selection change
   */
  handleCardSelected(detail) {
    if (!detail || !detail.card) return;

    const card = detail.card;
    const variantData = card.querySelector('.vpv-buy-box-card__variant-data');
    if (!variantData) return;

    // Update state from selected card
    this.variantId = variantData.dataset.variantId;
    this.sellingPlanId = variantData.dataset.sellingPlanId || null;
    this.isSubscription = variantData.dataset.isSubscription === 'true';
    this.isLinkedProduct = card.dataset.isLinkedProduct === 'true';

    // Store linked variants map if available
    if (this.isLinkedProduct && card.dataset.linkedVariants) {
      try {
        this.linkedVariantsMap = JSON.parse(card.dataset.linkedVariants);
      } catch (e) {
        this.linkedVariantsMap = null;
      }
    } else {
      this.linkedVariantsMap = null;
    }

    // Update option title and display
    const cardTitle = card.querySelector('.vpv-buy-box-card__title');
    if (cardTitle) {
      this.currentOptionTitle = cardTitle.textContent.trim();
      this.updateOptionDisplay();
    }

    // Update price
    const priceEl = card.querySelector('[data-vpv-current-price]');
    if (this.priceEl && priceEl) {
      this.priceEl.textContent = priceEl.textContent.trim();
    }

    console.log('[VPV Buy Bar] Card selected:', {
      variantId: this.variantId,
      sellingPlanId: this.sellingPlanId,
      isSubscription: this.isSubscription,
      isLinkedProduct: this.isLinkedProduct
    });
  }

  /**
   * Handle variant/color change
   */
  handleVariantChange(detail) {
    if (!detail) return;

    // Extract color from the variant change event
    let selectedColor = null;
    
    if (detail.swatch) {
      // Get color from data attributes first (most reliable)
      selectedColor = detail.swatch.dataset?.limited || 
                      detail.swatch.dataset?.color ||
                      detail.swatch.dataset?.value ||
                      detail.swatch.value;
      
      // Only use textContent as fallback if it's clean text
      if (!selectedColor && detail.swatch.textContent) {
        const text = detail.swatch.textContent.trim();
        if (!text.includes('<') && !text.includes('>') && text.length < 50) {
          selectedColor = text;
        }
      }
    }
    
    // Try other detail properties
    if (!selectedColor && detail.color) selectedColor = detail.color;
    if (!selectedColor && detail.title) selectedColor = detail.title;
    if (!selectedColor && detail.value) {
      // Make sure value isn't HTML
      const val = String(detail.value);
      if (!val.includes('<') && val.length < 50) {
        selectedColor = val;
      }
    }
    if (!selectedColor && detail.option1) {
      const opt = String(detail.option1);
      if (!opt.includes('<') && opt.length < 50) {
        selectedColor = opt;
      }
    }

    // Update current color and display
    if (selectedColor) {
      this.currentColor = selectedColor;
      this.updateOptionDisplay();
    }

    // For linked products, match color to linked variant
    if (this.isLinkedProduct && this.linkedVariantsMap && selectedColor) {
      const normalizedColor = selectedColor.toLowerCase().trim().replace(/\s+/g, '-');
      const matchedVariantId = this.linkedVariantsMap[normalizedColor];
      
      if (matchedVariantId) {
        this.variantId = matchedVariantId;
        console.log('[VPV Buy Bar] Linked variant matched:', normalizedColor, '→', matchedVariantId);
      }
    } else {
      // For regular products, update variant ID directly
      const variantId = detail.variantId || detail.id;
      if (variantId) {
        this.variantId = variantId;
        console.log('[VPV Buy Bar] Variant updated:', variantId);
      }
    }

    // Always sync price from active buy box card (it has the correct subscription price)
    // Small delay to let the card update its price first
    setTimeout(() => {
      this.syncPriceFromActiveCard();
    }, 50);
  }

  /**
   * Sync price display from the currently active buy box card
   */
  syncPriceFromActiveCard() {
    const activeCard = document.querySelector('.vpv-buy-box-card--active');
    if (!activeCard) return;

    const priceEl = activeCard.querySelector('[data-vpv-current-price]');
    if (this.priceEl && priceEl) {
      this.priceEl.textContent = priceEl.textContent.trim();
      console.log('[VPV Buy Bar] Price synced from card:', priceEl.textContent.trim());
    }
  }

  /**
   * Format price in cents to money string
   */
  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\.00$/, '');
  }

  /**
   * Get sections to re-render after cart update
   */
  getSectionsToRender() {
    const sections = [];

    const cartContent = document.getElementById('cart-drawer__content');
    if (cartContent) {
      sections.push({
        id: 'cart-drawer__content',
        section: cartContent.dataset.id,
        selector: '.cart-drawer__content',
      });
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      });
    }

    const cartHeader = document.getElementById('cart-drawer__header');
    if (cartHeader) {
      sections.push({
        id: 'cart-drawer__header',
        section: cartHeader.dataset.id,
        selector: '.cart-drawer__header',
      });
    }

    const cartFooter = document.getElementById('cart-drawer__footer');
    if (cartFooter) {
      sections.push({
        id: 'cart-drawer__footer',
        section: cartFooter.dataset.id,
        selector: '.cart-drawer__footer',
      });
    }

    return sections;
  }

  /**
   * Parse section HTML from response
   */
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML || '';
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart(e) {
    e.preventDefault();

    if (!this.variantId) {
      console.error('[VPV Buy Bar] No variant ID set');
      return;
    }

    // Disable button
    this.addButton.disabled = true;
    this.addButton.classList.add('vpv-loading');

    const item = {
      id: parseInt(this.variantId),
      quantity: 1
    };

    // Add selling plan if subscription
    if (this.isSubscription && this.sellingPlanId) {
      item.selling_plan = parseInt(this.sellingPlanId);
    }

    console.log('[VPV Buy Bar] Adding to cart:', item);

    const cartDrawer = document.querySelector('cart-drawer');

    // The drawer only opens once this request lands (see below) — the
    // button's own vpv-loading state (CSS: assets/vpv-buy-bar.css) is what
    // signals "something is happening" in the meantime, so there's no
    // stale/empty-cart flash to cover for with an early, data-less open
    // anymore.
    //
    // Participates in the same staleness guard cart-drawer.js's own mutations
    // use (see CartDrawer.beginRequest/isStaleRequest) — without it, this
    // request's eventual render could land after, and be silently reverted
    // by, a newer action elsewhere (e.g. the user removing something) that
    // has no way to know this one was ever in flight.
    const requestId = cartDrawer?.beginRequest ? cartDrawer.beginRequest() : null;

    try {
      const sectionsToRender = this.getSectionsToRender();

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript'
        },
        body: JSON.stringify({
          items: [item],
          sections: sectionsToRender.map(s => s.section),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Add to cart failed');
      }

      const data = await response.json();

      if (requestId != null && cartDrawer.isStaleRequest(requestId)) return;

      // Update cart sections via the shared helper (assets/cart-drawer.js) so
      // the footer gets its surgical patch — not a wholesale innerHTML swap —
      // keeping in-flight state (a discount code being typed, etc.) intact.
      if (data.sections) {
        sectionsToRender.forEach(section => {
          renderCartSection(section, data.sections[section.section]);
        });
      }

      // Opened only now, with the added item already painted into the
      // drawer's sections above — a normal open() (no skipSideEffects) is
      // safe here since the cart data is already current, and its own
      // "cart:updated" dispatch covers cart-protection-sync.js.
      this.openCartDrawer();

      // Dispatch events
      document.dispatchEvent(new CustomEvent('vpv:cart:item-added', {
        detail: { items: data.items || [data] }
      }));
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { items: data.items || [data] }
      }));

    } catch (error) {
      console.error('[VPV Buy Bar] Add to cart error:', error);
      
      document.dispatchEvent(new CustomEvent('vpv:cart:error', {
        detail: { message: error.message || 'Could not add item to cart.' }
      }));

    } finally {
      this.addButton.disabled = false;
      this.addButton.classList.remove('vpv-loading');
    }
  }

  /**
   * Open cart drawer
   */
  openCartDrawer(options) {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open(options);
      return;
    }

    // Fallback: click cart icon
    const cartIcon = document.querySelector('[data-cart-icon], .cart-icon-bubble, #cart-icon-bubble a, .js-open-cart');
    if (cartIcon) {
      cartIcon.click();
      return;
    }

    // Another fallback
    if (cartDrawer) {
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('is-open', 'active');
    }
  }
}

customElements.define('vpv-buy-bar', VpvBuyBar);

// Export for debugging
window.VpvBuyBar = VpvBuyBar;
